/**
 * The sections of Settings, grouped, down the left - and the heading of whichever is open.
 *
 * A NAV RATHER THAN THE TAB STRIP IT REPLACES, because the strip had eight names and a
 * custom agent's six, and adding "Our team only" as a ninth would have been a tab a member
 * could see was missing. Grouped, the reader's own groups are Business, Agent, Calls and
 * People, and ours is a fifth group with an accent title that simply is not there for them.
 *
 * A DOT SAYS WHERE THE UNSAVED CHANGES ARE, which is the job the old save status did in
 * words ("Unsaved changes on Business and Your agent"). With one bar for the whole page the
 * bar cannot name every section, so the nav does: warm for unsaved, alert for a part whose
 * save failed.
 *
 * UNDER 1000px IT IS A ROW THAT SCROLLS SIDEWAYS, because a 208px column beside a settings
 * pane on a tablet leaves the pane too narrow for its 220px label column. Group titles go;
 * our team's items keep an accent dot so they are still marked.
 */

import type { ReactNode } from "react";
import { NavTag, TeamOnlyBadge, cx } from "../../ui";
import { SECTIONS, type GroupId, type SectionId } from "./sections";

export type Marks = Partial<Record<SectionId, "dirty" | "error">>;

export function SettingsNav({
  groups,
  current,
  marks,
  tags,
  onPick,
}: {
  groups: Array<{ id: GroupId; title: string; sections: SectionId[] }>;
  current: SectionId;
  marks: Marks;
  tags: Partial<Record<SectionId, ReactNode>>;
  onPick: (id: SectionId) => void;
}) {
  return (
    <nav
      aria-label="Settings sections"
      className={cx(
        "sticky top-[calc(var(--spacing-bar)+var(--spacing-head)+16px)] flex flex-col gap-4.5",
        "max-[1000px]:static max-[1000px]:-mx-4 max-[1000px]:flex-row max-[1000px]:gap-1 max-[1000px]:overflow-x-auto max-[1000px]:px-4 max-[1000px]:pb-1",
      )}
    >
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-0.5 max-[1000px]:flex-row max-[1000px]:gap-1">
          <div
            className={cx(
              "px-3 pb-1.5 text-2xs font-semibold tracking-[0.12em] uppercase max-[1000px]:hidden",
              group.id === "ours" ? "text-accent" : "text-ink-faint",
            )}
          >
            {group.title}
          </div>
          {group.sections.map((id) => {
            const on = id === current;
            const mark = marks[id];
            return (
              <button
                key={id}
                type="button"
                aria-current={on ? "page" : undefined}
                onClick={() => onPick(id)}
                className={cx(
                  "flex items-center justify-between gap-2 rounded-sm px-3 py-2 text-left text-sm font-medium whitespace-nowrap transition-colors",
                  on ? "bg-sunken text-ink" : "text-ink-soft hover:bg-line-soft hover:text-ink",
                )}
              >
                <span className="flex items-center gap-2">
                  {group.id === "ours" && <span aria-hidden="true" className="hidden size-1.5 rounded-full bg-accent max-[1000px]:block" />}
                  {SECTIONS[id].label}
                </span>
                <span className="flex items-center gap-1.5">
                  {tags[id] !== undefined && <NavTag>{tags[id]}</NavTag>}
                  {mark && (
                    <span
                      className={cx("size-2 rounded-full", mark === "error" ? "bg-alert" : "bg-warm")}
                      title={mark === "error" ? "Not saved" : "Unsaved changes"}
                    >
                      <span className="sr-only">{mark === "error" ? "Not saved" : "Unsaved changes"}</span>
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/** The open section's name, our team's badge when it is ours, and its one sentence. */
export function PaneHead({ id, title }: { id: SectionId; title?: string }) {
  const info = SECTIONS[id];
  return (
    <div className="grid max-w-[620px] gap-1.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="text-xl font-semibold tracking-[-0.02em] text-ink">{title ?? info.title}</h2>
        {info.internal && <TeamOnlyBadge />}
      </div>
      <p className="text-sm text-ink-soft">{info.description}</p>
    </div>
  );
}
